import {
  CreateExternalDriverCommand,
  ExternalDriverResponseDto,
  UpdateExternalDriverCommand,
} from '@planning/app/api'
import { ExternalDriverItemStore } from '@planning/rt-stores/externalDriver/ExternalDriverItemStore'
import externalDriverService from '@planning/services/externalDriverService'

import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'

export interface ExternalDriverFormData {
  id?: number
  driverID: string
  name: string
  banned: string
  notes?: string | null
}

export class ExternalDriverViewStore {
  initialValues?: Partial<ExternalDriverResponseDto>

  isAddDialogOpen = false
  isDeleteDialogOpen = false

  dataToBeEdited?: ExternalDriverResponseDto

  addingCallback?: (externalDriver: ExternalDriverResponseDto) => void

  constructor(public readonly externalDriverItemStore: ExternalDriverItemStore) {
    makeObservable(this, {
      dataToBeEdited: observable,
      isAddDialogOpen: observable,
      isDeleteDialogOpen: observable,
      initialValues: observable,

      items: computed,

      toggleAddDialog: action,
      toggleDeleteDialog: action,
      setDataToBeEdited: action,
    })
  }

  get allItems() {
    return _.values(this.externalDriverItemStore.elements)
  }

  get items() {
    return _.values(this.externalDriverItemStore.elements).map(x => ({ ...x.data }))
  }

  setDataToBeEdited = (data?: ExternalDriverResponseDto) => (this.dataToBeEdited = data)

  toggleAddDialog = (
    shouldOpen: boolean,
    initialValues?: Partial<ExternalDriverResponseDto>,
    addingCallback?: (externalDriver?: ExternalDriverResponseDto) => void,
  ) => {
    this.isAddDialogOpen = shouldOpen
    this.initialValues = initialValues
    this.addingCallback = addingCallback
  }

  toggleDeleteDialog = (shouldOpen: boolean) => (this.isDeleteDialogOpen = shouldOpen)

  onDelete = async (id: number) => {
    this.toggleDeleteDialog(false)
    await externalDriverService.delete(id)
  }

  onSubmit = async (data: ExternalDriverFormData) => {
    const isEditMode = !!data.id

    if (isEditMode) {
      const cmd: UpdateExternalDriverCommand = {
        ...data,
        banned: data.banned === 'true',
        id: data.id!,
      }
      await externalDriverService.update(cmd)
    } else {
      const cmd: CreateExternalDriverCommand = {
        ...data,
        banned: data.banned === 'true',
      }
      const id = await externalDriverService.create(cmd)

      if (this.addingCallback) {
        this.addingCallback({
          id,
          banned: cmd.banned ?? false,
          driverID: cmd.driverID,
          name: cmd.name,
          notes: cmd.notes,
        })
      }
    }
  }

  onRemoveBan = async (data: ExternalDriverResponseDto) => {
    const cmd: UpdateExternalDriverCommand = {
      ...data,
      banned: false,
    }
    await externalDriverService.update(cmd)
  }

  onBan = async (data: ExternalDriverResponseDto) => {
    const cmd: UpdateExternalDriverCommand = {
      ...data,
      banned: true,
    }
    await externalDriverService.update(cmd)
  }
}
